import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSessionGuard } from '../../contexts/SessionGuardContext';
import { configApi } from '../../api/configApi';
import { SuitSymbol } from '../../components/common/SuitSymbol';
import { BidText } from '../../components/common/BidToken';
import {
  FAMILY_SHORT_TITLES,
  findModule,
  findTopic,
  isFamily,
  moduleName,
} from '../../config/moduleCatalog';

/**
 * The masthead brand — "Hawkes Bridge", the four suits, and the tagline.
 *
 * Rendered twice (desktop and mobile breakpoints show different elements
 * alongside it), so it lives here rather than being copy-pasted: the two copies
 * had already been kept in sync by hand, and the suits would have been a third
 * thing to remember.
 *
 * The suits are decoration, so the whole row is aria-hidden — without that the
 * link's accessible name reads "Hawkes Bridge spades hearts diamonds clubs",
 * since SuitSymbol labels each glyph for the contexts where it IS meaningful.
 */
/**
 * The running session's scoring type, as a felt chip.
 *
 * Felt-on-cream rather than the red these pages used to print: the bar already carries a line or
 * two of dark text, and a third colour beside them read as an alert instead of a label. The chip
 * echoes the felt strip immediately below it, and cream on felt is 8:1 — comfortably past AA, where
 * `text-red-600` on cream sat at 4.6:1 and only just cleared it.
 *
 * Shared by both practice surfaces (feature 023) so the phone header says the same thing the same
 * way whichever one is running. `header-scoring-chip` is the hook the header's grid and the compact
 * header bands use; inside the module trail it also carries the chip's grid placement.
 */
const SCORING_CHIP =
  'header-scoring-chip shrink-0 rounded-full bg-felt px-2.5 py-0.5 text-xs font-semibold leading-[16px] text-cream';

/**
 * The place-in-the-course trail for `/module-practice/:family/:topicKey/:moduleSlug`, shown
 * in the mobile header in place of the two-line brand (which the fullscreen pages' compact
 * header height would clip on short phones). Split in two so the header can set the family
 * and topic apart from the module being practised. Null off the module-practice route or
 * for an unknown module — those URLs redirect away anyway.
 */
function modulePracticeHeaderTitle(
  pathname: string
): { trail: string; module: string } | null {
  if (!pathname.startsWith('/module-practice/')) return null;
  const [, , family, topicKey, moduleSlug] = pathname.split('/');
  if (!isFamily(family)) return null;
  const topic = findTopic(topicKey);
  const module = topic ? findModule(family, topic, moduleSlug) : undefined;
  if (!topic || !module) return null;
  return {
    trail: `${FAMILY_SHORT_TITLES[family]} · ${topic.title}`,
    module: moduleName(module, topic),
  };
}

/**
 * The session summary's place in the course, for the same mobile header slot (spec 031, client
 * request, 2026-10-05). The summary is the page the learner lands on straight off the table, so
 * its phone header carries the trail the table's header did instead of the two-line brand, which
 * cost the card ~60px of a 303px landscape phone. A module visit reads exactly as module practice
 * did (family · topic over the module); a problems or counting visit splits the name its own
 * masthead used at the first '·' ("Competitive Bidding" over "The overcalling side"), or shows
 * it alone when it has no parts ("Counting").
 *
 * Read from the router state the summary itself draws from — the URL carries no trail. Null
 * without state; that visit redirects away anyway.
 */
function sessionSummaryHeaderTitle(
  pathname: string,
  state: unknown
): { trail: string | null; module: string } | null {
  if (pathname !== '/session-summary' || !state || typeof state !== 'object') return null;
  const s = state as {
    kind?: string;
    family?: string;
    topicTitle?: string;
    moduleTitle?: string;
    trail?: string;
  };
  if (s.kind === 'problems' || s.kind === 'counting') {
    if (!s.trail) return null;
    const cut = s.trail.indexOf(' · ');
    return cut < 0
      ? { trail: null, module: s.trail }
      : { trail: s.trail.slice(0, cut), module: s.trail.slice(cut + 3) };
  }
  if (!isFamily(s.family) || !s.topicTitle || !s.moduleTitle) return null;
  return { trail: `${FAMILY_SHORT_TITLES[s.family]} · ${s.topicTitle}`, module: s.moduleTitle };
}

function Brand({ className }: { className: string }) {
  return (
    <Link to="/" className={`${className} flex-col justify-center leading-tight`}>
      {/* Georgia/serif brand with the suits set ~3/4 its size and letter-spaced,
          per `.brand` / `.brand .suits` in the reference design. The suits keep
          the shared SuitSymbol's black/red coding rather than the design's
          four-colour scheme — the constitution's Suit Symbol Display standard
          mandates the shared component. */}
      <span className="flex items-baseline">
        <span className="font-serif text-2xl font-bold text-primary-600">Hawkes Bridge</span>
        <span className="ml-1.5 flex items-baseline text-lg tracking-[1px]" aria-hidden="true">
          {/* The brand's suits are sized against the wordmark, not the site-wide glyph
              size — the one place that exemption applies. */}
          <SuitSymbol suit="S" className="suit-glyph-inherit" />
          <SuitSymbol suit="H" className="suit-glyph-inherit" />
          <SuitSymbol suit="D" className="suit-glyph-inherit" />
          <SuitSymbol suit="C" className="suit-glyph-inherit" />
        </span>
      </span>
      <span className="text-sm max-[374px]:text-xs italic font-serif text-ink-soft">
        Study Precision. Practice Competitive Bidding.
      </span>
    </Link>
  );
}

export function Header() {
  const { isAuthenticated, logout, user, accessibleFeatures } = useAuth();
  // Feature 011 (FR-035): Admin link visible only to Admin role.
  const isAdmin = user?.role === 'Admin';
  // Feature 025: the Super Editor always has a Data Editing surface — "Ask Jeff — Review"
  // is role-gated rather than grant-gated, so it never appears in accessibleFeatures and
  // the grant test below would hide the link on an account holding no other grants.
  const isSuperEditor = user?.role === 'Super Editor';
  // Feature 011 (FR-036): Data Editing link visible iff Admin OR the user has
  // at least one accessible feature. Replaces the legacy dropdown (FR-046).
  const canSeeDataEditing = isAdmin || isSuperEditor || accessibleFeatures.length > 0;
  const { hasActiveSession, logoutInterceptor, scoringType, sessionTitle, sessionTitleTrail } =
    useSessionGuard();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Dev-only: Viewport size display (controlled by server config ShowViewportSize)
  const [showViewportSize, setShowViewportSize] = useState(false);
  const [siteUnderConstruction, setSiteUnderConstruction] = useState(false);
  const [viewportSize, setViewportSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const viewportConfigFetched = useRef(false);
  const headerRef = useRef<HTMLElement>(null);

  // Publish the masthead's real rendered height as --app-header-h, so the viewport-locked
  // pages can size their scroll areas from what the bar actually measures instead of
  // assuming the 4rem it used to be pinned to (see .problems-scroll-height in index.css).
  // Needed since the bar grows to fit a wrapped brand from 768px up.
  //
  // getBoundingClientRect(), not offsetHeight: the latter rounds to whole pixels, and half a
  // pixel of rounding is the difference between a locked page that cannot scroll and one that
  // technically can — which is what the e2e no-overflow checks assert on.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const publish = () =>
      document.documentElement.style.setProperty(
        '--app-header-h',
        `${el.getBoundingClientRect().height}px`
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--app-header-h');
    };
  }, []);

  useEffect(() => {
    if (viewportConfigFetched.current) return;
    viewportConfigFetched.current = true;
    configApi.getClientConfig().then(cfg => {
      if (cfg.showViewportSize) setShowViewportSize(true);
      if (cfg.siteUnderConstruction) setSiteUnderConstruction(true);
    }).catch(() => { /* default off */ });
  }, []);

  useEffect(() => {
    if (!showViewportSize) return;
    const handleResize = () => setViewportSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showViewportSize]);

  // Close mobile menu when route changes (FR-042a)
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside (FR-042c)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isMobileMenuOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // FR-109 (v1.9): Check for active session before logout
  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    // If there's an active session and an interceptor is set, call the interceptor
    // instead of logging out immediately
    if (hasActiveSession && logoutInterceptor) {
      logoutInterceptor();
    } else {
      logout();
    }
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Fullscreen pages show a subbanner / page title instead of the "Hawkes Bridge"
  // brand + tagline. Only the banner pages get the taller header on short phones
  // (see .header-with-banner in index.css); fullscreen pages keep the compact
  // header they depend on for their scroll-height math.
  //
  // Feature 029: the conceptual-problem BROWSING page is one of them — it is drawn on the same
  // viewport-locked table frame, and its lock subtracts the compact 2rem bar. Without this it
  // rendered the 3.75rem banner instead and overran the viewport by exactly the difference on a
  // short portrait phone (measured 28px at 375x547). The category list above it is an ordinary
  // page and keeps the banner, hence the trailing slash — the same cut Layout's route list makes.
  const isFullscreenPage =
    location.pathname === '/problems' ||
    location.pathname.startsWith('/module-practice/') ||
    location.pathname === '/counting-practice' ||
    location.pathname === '/howard-hands' ||
    location.pathname.startsWith('/conceptual-problems/') ||
    // Not viewport-locked like the others, but it carries a session trail in this slot rather
    // than the brand (sessionSummaryHeaderTitle below), so it takes the same compact bar.
    location.pathname === '/session-summary';

  // Same test, named for what the title slot below asks: is this the conceptual BROWSING page?
  const isConceptualProblem = location.pathname.startsWith('/conceptual-problems/');

  // Feature 028, round six: the re-themed Counting page joins module practice in showing the
  // BRAND on a portrait tablet rather than its own page title (client request, 2026-09-03).
  // A tablet has the width for the wordmark and the room for the tagline, and the frame's own
  // masthead names the session ("Counting") in the band directly below, so a page title in the
  // bar was spending a tablet's spare width repeating what the next row already says.
  // Landscape tablets (>= 1024 wide) fall outside `mobilenav` and have always shown the brand,
  // so `tablet-pt` is the whole of the tablet gap.
  const isCountingPractice = location.pathname === '/counting-practice';

  // Module practice's mobile header title (null everywhere else).
  const moduleTitle = modulePracticeHeaderTitle(location.pathname);

  // The session summary's mobile header title (null everywhere else).
  const summaryTitle = sessionSummaryHeaderTitle(location.pathname, location.state);

  // Module practice's scoring type, as a felt chip inside the trail (client request,
  // 2026-07-29). It rides in the trail rather than beside the hamburger as a third column
  // because on a portrait phone the course trail's first line is long and the module name's is
  // short: parking the chip in that leftover space costs the trail nothing, where a column of
  // its own cost it ~38px and clipped "16+ points" off the topic on a 360px phone. Landscape
  // has the width to spare, so there the grid centres it against the whole block instead.
  const scoringChip =
    moduleTitle && scoringType ? (
      <span data-testid="header-scoring-type" className={SCORING_CHIP}>
        {scoringType}
      </span>
    ) : null;

  return (
    // Matches `.light .mast` in the reference design: cream masthead over the
    // paper page, partitioned by a solid brass rule instead of the old white
    // bar + drop shadow.
    <header ref={headerRef} className="bg-cream masthead-rule sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`relative flex justify-between h-16 main-header-height${isFullscreenPage ? '' : ' header-with-banner'}`}>
          {/* Logo and brand */}
          {/* min-w-0 so the module-practice title's truncation can bite inside this
              justify-between row instead of pushing the hamburger off-screen. That title also
              claims the whole row (flex-1) so its long first line runs right up to the
              hamburger — the scoring chip rides on the second line, in the space the short
              module name leaves behind, rather than shortening both lines. */}
          <div className={`flex items-center min-w-0${moduleTitle || summaryTitle || isConceptualProblem ? ' flex-1' : ''}`}>
            {/* Desktop: always show brand. A portrait tablet counts as desktop for module
                practice — it collapses the nav links into the hamburger (mobilenav) but has
                the width for the brand and the room for its tagline, so tablet-pt puts it
                back in place of the course trail below. Conditional on `moduleTitle` because
                the other fullscreen pages (/problems, counting practice) show a page title in
                that same slot at these widths; revealing the brand there too would print both
                side by side. Those pages are unchanged. */}
            <Brand
              className={`hidden md:flex mobilenav:hidden${moduleTitle || summaryTitle || isCountingPractice || isConceptualProblem ? ' tablet-pt:flex' : ''}`}
            />
            {/* Mobile: page title on fullscreen pages during active session. The running session
                publishes its own title (feature 023), so the banner reads exactly what the frame's
                masthead does — "Beginner · Bidding Practice" for a level session. The fallback
                covers the instant between the guard going live and the title being published.
                Truncates on ONE line rather than wrapping: naming the level made this string ~80%
                longer, and the compact bar these phones get is 32px tall (see the <360w and short
                bands in index.css), so a second line does not overflow the bar — it is clipped by
                it. A step down in type at the narrowest widths buys most of the level name back,
                and the untruncated title is still announced from the frame's own h1. */}
            {location.pathname === '/problems' && hasActiveSession ? (
              sessionTitleTrail ? (
                /* A session that names itself in two parts (Overcalling: "Competitive Bidding"
                   over "The overcalling side") borrows module practice's course-trail grid, so
                   the muted-family-over-bold-name shape is the same one on both surfaces. No
                   scoring chip rides in this one — on /problems it has its own slot below. */
                <span
                  data-testid="session-trail"
                  className="module-trail grid md:hidden mobilenav:grid w-full min-w-0"
                >
                  <span className="module-trail-crumb truncate text-xs leading-[14px] font-medium text-ink-soft">
                    {sessionTitleTrail}
                  </span>
                  <span className="module-trail-module truncate text-sm leading-[16px] font-bold text-ink">
                    {sessionTitle}
                  </span>
                </span>
              ) : (
                <span className="md:hidden mobilenav:inline-block min-w-0 truncate text-lg xnarrow:text-base font-bold text-ink">
                  {sessionTitle ?? 'Bidding Practice'}
                </span>
              )
            ) : isCountingPractice ? (
              /* Phones only — `tablet-pt:hidden` hands the slot back to the brand above, the
                 same swap module practice's trail makes. */
              <span className="md:hidden mobilenav:inline tablet-pt:hidden text-lg font-bold text-ink">
                Counting Practice
              </span>
            ) : location.pathname === '/howard-hands' ? (
              <span className="md:hidden mobilenav:inline text-lg font-bold text-ink">
                Hands for Howard
              </span>
            ) : isConceptualProblem ? (
              /* Feature 029. Like the other fullscreen pages, this slot carries the page's own
                 title on phones rather than the brand: the bar is 32px on the short bands and the
                 two-line brand banner is clipped by it (measured at 375x547).

                 It names the CATEGORY as well as the surface (client request, 2026-09-09), in the
                 same two-line shape module practice uses for its course trail — the muted crumb
                 over the bold name — so an editor reads where they are the same way on both
                 surfaces. It borrows that trail's grid outright (`module-trail`), which is what
                 gives it the per-band behaviour for free: one line with a '·' separator on a short
                 landscape phone, and no row gap on the 32px bars. See index.css.

                 The category cannot be derived from the address the way the module trail is — the
                 route carries the category's id, not its name — so the page publishes it through
                 the session guard, and this falls back to the surface name alone until it lands
                 (and in every state that has no category: loading, error, not-found, empty). */
              sessionTitle ? (
                <span
                  data-testid="conceptual-trail"
                  className="module-trail grid md:hidden mobilenav:grid tablet-pt:hidden w-full min-w-0"
                >
                  <span className="module-trail-crumb truncate text-xs leading-[14px] font-medium text-ink-soft">
                    Conceptual Problems
                  </span>
                  {/* Plain text, NOT BidText: a category name carries real suit glyphs already
                      ('System sequences after 1♣ or 1♦') and strings like '2NT' that BidText would
                      re-render, leaving the header disagreeing with the frame's own h1 below it. */}
                  <span className="module-trail-module truncate text-sm leading-[16px] font-bold text-ink">
                    {sessionTitle}
                  </span>
                </span>
              ) : (
                <span className="md:hidden mobilenav:inline tablet-pt:hidden text-lg xnarrow:text-base font-bold text-ink">
                  Conceptual Problems
                </span>
              )
            ) : moduleTitle ? (
              /* The whole place in the course: a muted trail (family · topic) and the bold
                 module being practised. Sized to fit INSIDE the fullscreen pages' fixed
                 header — 2rem on short phones, and .problems-scroll-height subtracts what the
                 bar actually measures — so the title adapts to the bar, never the other way
                 round. Each part truncates rather than wrapping another line in.

                 Three flat children (crumb, module, chip) placed by a grid in index.css, which
                 rearranges them per band: stacked with the chip beside the module on portrait
                 phones, stacked with the chip centred against both lines in landscape, and all
                 three on ONE line where the bar is short but wide. Flat rather than nested so
                 the grid can move any part without the DOM having to change with it — keep the
                 crumb first, though: it is the line the truncation tests measure.

                 Phones and landscape tablets only. This trail exists because a phone's header
                 has no room for both it and the brand; a portrait tablet has the room, and its
                 frame masthead already names the whole trail in full, so it shows the brand
                 instead (client request, 2026-07-29). The scoring chip is inside this block and
                 goes with it — at those widths the status line under the frame carries it. */
              <span
                data-testid="module-trail"
                className="module-trail grid md:hidden mobilenav:grid tablet-pt:hidden w-full min-w-0"
              >
                {/* 12px is the site's floor for body-adjacent text and the players are mostly
                    older, so the trail does not go below it however tight the bar gets. Line
                    heights are pinned in pixels, not ratios: 14 + 16 = 30px, which is what lets
                    the pair sit inside the 2rem bar the short-phone rule imposes. The 3px of air
                    between them fits the ordinary 4rem bar; the compact-header media queries in
                    index.css take it back out, where those 30px ARE the bar. */}
                <span className="module-trail-crumb truncate text-xs leading-[14px] font-medium text-ink-soft">
                  <BidText text={moduleTitle.trail} />
                </span>
                <span className="module-trail-module truncate text-sm leading-[16px] font-bold text-ink">
                  <BidText text={moduleTitle.module} />
                </span>
                {scoringChip}
              </span>
            ) : summaryTitle ? (
              /* The session summary: the trail the table's header carried, in module practice's
                 own grid, so the bands in index.css lay it out the same way (one line with a '·'
                 on the short landscape phones). Shown where the module trail is; the card hides
                 its own copy of the trail at exactly these sizes (index.css), and a portrait
                 tablet keeps the brand, as on the practice pages. */
              summaryTitle.trail ? (
                <span
                  data-testid="summary-header-trail"
                  className="module-trail grid md:hidden mobilenav:grid tablet-pt:hidden w-full min-w-0"
                >
                  <span className="module-trail-crumb truncate text-xs leading-[14px] font-medium text-ink-soft">
                    <BidText text={summaryTitle.trail} />
                  </span>
                  <span className="module-trail-module truncate text-sm leading-[16px] font-bold text-ink">
                    <BidText text={summaryTitle.module} />
                  </span>
                </span>
              ) : (
                <span
                  data-testid="summary-header-trail"
                  className="md:hidden mobilenav:inline-block tablet-pt:hidden min-w-0 truncate text-lg xnarrow:text-base font-bold text-ink"
                >
                  <BidText text={summaryTitle.module} />
                </span>
              )
            ) : (
              <Brand className="flex md:hidden mobilenav:flex" />
            )}
          </div>

          {/* Desktop navigation links - hidden on mobile (FR-039) */}
          <div className="hidden md:flex mobilenav:hidden items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                >
                  Dashboard
                </Link>
                {/* Feature 023 (FR-005a/FR-005b): the "Problems" dropdown is gone — its three
                    destinations are dashboard cards now. Study re-homes the content of the
                    retired dashboard tab. Practice has followed it onto the dashboard: its one
                    remaining surface, Overcalling, is a door card of its own. */}
                <Link
                  to="/learn"
                  className="text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                >
                  Study
                </Link>
                {canSeeDataEditing && (
                  <Link
                    to="/data-editing"
                    className={
                      isAdmin
                        ? "text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                        : "bg-primary-50 text-primary-700 hover:bg-primary-100 ring-1 ring-primary-400 px-3 py-2 rounded-md text-base font-medium"
                    }
                  >
                    Data Editing
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  className="text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                >
                  Account
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-ink-soft hover:text-ink px-3 py-2 rounded-md text-base font-medium"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary text-base"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile scoring type display - shown during active session on scoring-bearing
              practice pages. Module practice is absent HERE because it renders its own chip
              inside the course trail above (see `scoringChip`) — it has no session guard, and
              the trail's second line is a better home than a column of its own.
              Feature 023: the same felt chip the trail uses, not the old red text — /problems now
              sits on the same felt as module practice, and two different treatments of one label
              in one bar read as two different kinds of thing. */}
          {scoringType &&
            hasActiveSession &&
            (location.pathname === '/problems' || isCountingPractice || location.pathname === '/howard-hands') && (
            <span
              data-testid="header-scoring-type"
              /* Feature 028: the re-themed Counting page drops the chip on a portrait tablet,
                 for module practice's reason — that band shows the brand, and the status line
                 under the frame already carries the scoring type, so keeping it here prints
                 "MPs" twice on one screen. Every other page in this list is unchanged. */
              className={`${SCORING_CHIP} md:hidden mobilenav:inline-block self-center${isCountingPractice ? ' tablet-pt:hidden' : ''}`}
            >
              {scoringType}
            </span>
          )}

          {/* Mobile hamburger menu button - visible only on mobile (FR-040) */}
          <div className="flex items-center md:hidden mobilenav:flex">
            <button
              ref={buttonRef}
              type="button"
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-ink-soft hover:text-ink hover:bg-paper focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {/* Hamburger icon / Close icon (FR-043) */}
              {!isMobileMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
          {/* Dev-only: Viewport size inside header bar (problems / counting / howard pages) */}
          {showViewportSize && (location.pathname === '/problems' || location.pathname === '/counting-practice' || location.pathname === '/howard-hands') && (
            <div aria-hidden="true" className="absolute bottom-0 left-0 px-4 text-xs font-bold text-red-700 font-mono leading-tight">{viewportSize.w} x {viewportSize.h}</div>
          )}
        </div>

      </nav>

      {/* Under construction banner — shown only before login */}
      {siteUnderConstruction && !isAuthenticated && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-1.5 text-center">
          <span className="text-sm font-medium text-yellow-800">
            &#9733; Website is under construction &#9733;
          </span>
        </div>
      )}

      {/* Mobile menu full-screen overlay (FR-041) */}
      {isMobileMenuOpen && (
        <div
          ref={menuRef}
          id="mobile-menu"
          className="md:hidden mobilenav:block fixed inset-0 z-50 bg-paper"
        >
          {/* Close button in top right */}
          <div className="flex justify-end p-4">
            <button
              type="button"
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-ink-soft hover:text-ink hover:bg-cream focus:outline-none"
              aria-label="Close menu"
            >
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Menu items left-aligned */}
          <div className="flex flex-col space-y-2 px-4 pt-4">
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={handleLinkClick}
                  className="text-ink-soft hover:text-ink text-xl font-medium py-3"
                >
                  Dashboard
                </Link>
                <Link
                  to="/learn"
                  onClick={handleLinkClick}
                  className="text-ink-soft hover:text-ink text-xl font-medium py-3"
                >
                  Study
                </Link>
                {canSeeDataEditing && (
                  <Link
                    to="/data-editing"
                    onClick={handleLinkClick}
                    className={
                      isAdmin
                        ? "text-ink-soft hover:text-ink text-xl font-medium py-3"
                        : "bg-primary-50 text-primary-700 hover:bg-primary-100 ring-1 ring-primary-400 rounded-md px-3 text-xl font-medium py-3"
                    }
                  >
                    Data Editing
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={handleLinkClick}
                    className="text-ink-soft hover:text-ink text-xl font-medium py-3"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/account"
                  onClick={handleLinkClick}
                  className="text-ink-soft hover:text-ink text-xl font-medium py-3"
                >
                  Account
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-left text-ink-soft hover:text-ink text-xl font-medium py-3"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={handleLinkClick}
                  className="text-ink-soft hover:text-ink text-xl font-medium py-3"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={handleLinkClick}
                  className="btn-primary text-xl px-8 py-3 mt-4 text-center"
                >
                  Sign Up
                </Link>
              </>
            )}

            {/* "More" section — surfaces the Accessibility link in the mobile
                hamburger so it is reachable from viewport-locked pages where
                the desktop footer is suppressed (010-accessibility-seo-basics, FR-017). */}
            <div className="pt-6 mt-2 border-t border-line">
              <p className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">
                More
              </p>
              <Link
                to="/accessibility"
                onClick={handleLinkClick}
                className="text-ink-soft hover:text-ink text-lg font-medium py-2 block"
              >
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
